import { assign, createMachine } from 'xstate';

import { VideoNarrationDetail } from '@distribute/shared/types';

import { NarrationMachineActions, NarrationMachineContext } from './types';

import {
  onClear,
  onPause,
  onRestart,
  onResume,
  onStart,
  onStop,
} from './machine-actions';
import { getMediaStream } from '../lib';
import {
  createMediaRecorder,
  generateUploadSession,
  transformChunks,
  uploadChunk,
  uploadChunkFinal,
} from './machine-services';

const initialContext = {
  narration: {} as VideoNarrationDetail,

  teamId: null,
  pageId: null,

  videoId: '',
  audioId: '',

  uploadId: '',
  uploadUrl: '',

  mediaRecorderProxy: null,
  mediaStream: null,
  supportTracks: [],
  blobs: [],
  buffer: new Uint8Array(),
  lastBlobIndex: 0,
  lastBufferChunk: 0,
  isCancelled: false,
  isCancelledOnStart: false,
  isCancelledOnNetworkError: false,
  retry: 0,
} as NarrationMachineContext;

export const narrationMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiASQDlaAVAbQAYBdRUABwHtYuAC64++biAAeiAGwAmADQgAnogAsADjYaSAZgDsARn1qArAE5Ta-TI0BfO0rRY8hUgWHUIYsGXwA3PgBrX2ccAmI-YQQCQMx0ETF2DmSJfkFE8SQpRF0DEhkZNTZdOTZzDQ1DU319JVUEXVM5XQK5c0NqwvM2fTk5BycMcLcooWowACdJvkmSHgoEgDNZ1BIw10iPIRiAvnjM5NTs9OFRLNBpRvzC4tLyyura+vUNaxIHg0L9c11DXUGIA2EVIqEguHQAFFprNqMdeAIzmIJFdmi8EGpzDYSDVTCVzHILPJAcDRmCIBCAEpgTCzCBTLw+EiwIQJULDTag8Hoam0yb0ybwkCnTIoxDGHS6DRyH7WMpsNiaUzoqo4hUlGRY-RsUyGNQDRxAjkgkjkqk0ulTMiUGgAYUpkIAgkxIQARIUi85ihCykiGSwGGV68psRQqRByWz6EjWfSmUoaXRy8wk41k7m8y1zLAifw0byEZmsoTslwms08i38q053B5j2I0XZK55Ql+0qGWzVLRFdFyQxyHTmTUdtRqToWVNl9MUyt8gUkWt56j0SFMADqAHlKQBpAD6kMplO3DYyXubuVKpnb-a7ph7anRpV+MalJgHJnHZinI0iFcz1bZpgua+JMVYLuQVDUAAykwjqUqwnBpI256XOK44yCQPRFJqljlBY6J6lYJDaqUhL9LoMgmKYP6cqaGbgTWwF1qBjFzJBNCwZuAAKp5IhcOQIHqnZYWwOHmHhljmIRpi6n6pjyNKOpaCUai0eWDHzkxIEkGBWlzDw6AAK6wJA1AOtBACqACykJ8U2aFCQO5h6JR3Z5LoElvIRfxsFhNgyEmRgGBocbqTO5r6YuzF5rpbHzMZpkQDBTA8fZqGCZ07SuTI7l5F5j7hkJfSGCQiYyGw-rFBJvThX+mlZtFOl6Y1hkmWZtqOvQtqQgAMulyIXk52V5Ll94eQVhFVDog49DYchji0xKGqS9WzgBC5LqxUVtUl5mQrB8GIVwJwoYNjlZS5o15Z5FSFQ0epJn63x3P8lEyHVXLrfFW1xVFkxGfg+AEFA1DcY6lnQXZSGnWe52ZRhoniZJBFFZ0VEkISlVie0hT9PYK1pmtkWNb9LWAbpgPA-goNcbxMMInDAlXJ2UplUS82ydKpQ+f8MYBUF-z6KFNGE9OxNzqTMXbY1tKoAsYAlslA3M+KGgdGVY66NoCno6GhFUXImMElUSYSVYiqffR31RWT8VywrStwoYJ2M-x3qGOrpVvGo2saLrnb62j97Rj8MoKZGZE2Fb-4-dLcWwEZGAAEZUJZCx8OgArUAAQr1m45yr3plHGHz+30SaEniMiERUpWavG+qVXklti7+X0kxTduJynacZ1nVocSlR17pZ3H546roMAA4kXQ2GCGfrFPoTQ1ERth9sLmEtLqlQt3iagfW3dGx7b8dgT36Cp2A6cUJnEE2ilaUM8KZ2q0J2h+cGK+yUYZgb0VYoh8Yy9DjN8LQGhCgxwal3c+cAk5Xz7nfAeQEQKMkLCyNk6wiYd0lrA5q8De4337ptaWc8LoKnrsvX2JVzCH3RL7LQIDZTSnaNYUWQxxa4I2tpFiCcEHX1vvfXhy5OrdT6uQzKlUqG9Bof2OhNdAHPlEiYH46tPz6GgTbKWBDL6CJISImgFk4IIUkSzRe39V5-wUhoTeTQSJm1sJRX4MgFJaM7qQ3RAikHCNQXwmAhBJgJBBkIrO0E4CCDEOg3wsRgilnbtbDxhj+FENCZ4-xYBAnBJpmk8JsBIn4F2HEYJSROBmPFApaMnsbBNDeFJMwfZ+hGz1KFfsxhIyEgBMfDS2j8F8Ivt44hyD0mxQCVMbJUBckRPOBMGEBlFhCBWJMNYq1uFxy8akgxfjRmZPGSIHJJC8kFKKfsEp+Ajgv09PDFmlS-ShUCveTEFgGlFRaPqHEVgOiYm+KUTR3SIp4JGaxPRPiUFNQyVk-ZkzDnTKiXTcpQk8jXnvBVSi41MT6ifJ0VoVhD49gVH8ao7jAXJIGZs4ZyT6SLGUFM-JMz4WXLfh7WSRsrC2GHDUiq0lXkSSNrKMcRRK5YgNJwhJp8dH9MIYgoZvjwWxSEEE-AsAlmoBBtEvwgQQjYK4Ykkl2zgWDLSckhV6AlUqpBicg45wLmu1fkzZl2UiiQI1PGQldRXnqyuuOfs9w7gpn+RLHh+qUnSqNcGnANIghpJBtBYsNAEVPFKpVGoLRjChkTE+VxLlQy1ECpVLQnliVBrlQa8lsrfoRswFGkhMa43O1tVc9+iaPhPFTdqQcugnxWGvBVMwFR5qaCLesyVIKZVgt+kZGtNNbTYEBkEdVsStWrN1cW7uhqtklpIJO4ZIMZ1zstWcm1yF7XzzRTiZ1aLEwYrDA0JM2gPgCuaP6HUuUh1nw2aGjdE6p1QD3fgedUwZjzOWKsbVYqYFApDfoilwbt3313bO-9B7DhlMZSei6c0PieX6MGBSOonz6laMOR62sihYn1G+iVsUyWfpg5uuDWcENzoAGIEHQBQdVmCSxgZPhB0lUroPlvjgxik07ENBFY-gdjCL2gtDLrJBUvw6GGEzfeTGubpS-CeMtUVvHemQZo4J8dwmf1-ok2xig1ooIFhiXsJdOCV3DuowJ0FkGRNMf-ZJ9jVmwDIetahhtTKho4evEYR4YlqhSkjJmz2mNOjVE9jKQklG+nOdHWG+jpnxNecs0PGzGq4k8Z6Uk4NhnXPJPc2JljFmfN+dKSkF2x73bzxqezYwEcFpJn9l2rEr5hbaCaH8IwKWDMubHW5rL1WpO5cfoB2Y8wFkqqKwC1dcD0tfpMzuqrnmascTq+cgLTWHKCRCyRT2VQIvxmlIo29Rh66Cv9jqOMA2rYOyoE7BNFQ9DSllH0dUSoCNJvVPeX+FQigcKNDqt7iszIsEa7DZrjkyiSh+3Qv7CoAeAIUh8dUuUSj+n+P63TJpocfbkIF9DJ371Rd+-KRU3Wiq6zVAqQKhIfaVFe3weW73Ye6Ap4jzKlRvsyjR3TzHDR+iYXVC+7QxRJwBtIKTyA65sCZNtKazAYAKBUAgJufA9BFYAHdZhBGhEBuEaGBdXD6Nm0iBJqJmAWgRvyWNFQvv1Hizn3OYcQBV2rjXWudd69jegSY4wEV9B0BYAwddUVtIYbJTGeJlK6yaL7L3jtleq-wOr-Amvtew8t8dq4tg-Ir19vqSLY5UYNDwyRZPjuijag6Bnnnvvs+SdwLAVXEBo00wt-z4viBtSYVkofFeTwnudteW2V3UoQx-F+AaQ0+A+D0ngNkVZR2MpXAALQ3cQLv68EkT+n5P6wq2HFt-XMQFe+SgUsqhR6JiBhmJRIKldQpGoxQj7E9GNsa-JtFeAoZNIMfsP7A-DECBPQTQfUOpRUPoYlM3WYQA70KiK6aPX4OpVxf4BhFxeSRMRMBUdWIoEbKYVAoaPGAoAMCBcHH4FUBeevD-H7K9UoMg9iG0CgxyIwPyBuefSBMweg15HgrCC2OMf+GwInSHcDfTQxLgwSdWXg6hQMKqSAuha8V3QKNRSwEwdgzdcmAUeQq4LAgoZQ2hehNGXoFyZ7MSLTHQzsPQu2KKK-BHIfRFPIEBJvC7PUawSAlpNQHEYcfULUEwfoNSBXRzd9SVHaRKSAIwxAMHUw2RFQhRQiMob2X4UjCcb1CHZdcVVLGWCmAGIGEGeIoSZobNeQH4MfPoGUWxNGUaUQ0oTyOAz5AmP-QNJzQohcJXCAMo4wCqGMXKKUYcX+Bed1B6CwI2PtfUPNQVYWRwuBKKXov3fAYPIQPgHgMosoYiUvcA4I4WFTYOXKY2JLZTe8XoQwRYghZYrnTPdvTJPXXPfPbYnUAIvY4wA4qoA2TUU42ocoFoHof4a4kdddGDMouhVoIoZI8wyAsod4FpJvfsf2FoEEtLME2VFwt2NwiSGREwFIiwiXT8AoBeHWR3QMXIhzfI0bdbOjLafomUaMFFF1K9OhG9CMf4d4zQCBKUBef0NE0tWjITHSMZIJKFWlApfoq8c9VFVxVkzFV5JMJNP4GhZ8XUK4iI6k-jWk4UvhKldAGlGFOleGRtD2JoPyZky9M2BUiXCSVoNhGoCLcjEVaQvTErfQsbDLX6E1M1VYUo1wnfcUSiC0i9OU609khAGUX4hUYWLySMPkyknVLU0rT0jbHSStatLbKAEPEsKUwYy0sM69LtPEdTAcWwLQBSKQvIvjFMnU4zHSSrX9cTfoleVof2dreQTrcXXIfUaYmPMfRk5oRMmQ90tdMtesvUrXA0vvJsudbYhaHQcvMwdRcoSwZUIqJMMwLCEKaoXUYodWcIjotZKI9E8cibLMszHLFs6oNrMBMIrrdc29PEFyX4WM2SMaVxAUqDcrWDSbHbabHzfozDOMW6CoOpGxTNSwMqDoRUdyYIrpI8yIqjQUozc8+DbbczAC-Uw0i87LCzbYooAIuRQ+CoUMTyafW7DwooBM0KOMYEzUmsj0ustCxjDCnLEgXogiscPrBaTULQQEii3IQKIcAwCfYcaRJoL8srcbZJTigMm-RoBAkiTEGPDUJvTNDGZoWoKUJxV1KS1MujOS7EwMpySiPQMHB-DCVRBhEKeSNRb-UMCoYct0vVDiu4tvMon+agq7bQOg7lBoawToAg5SbUTQBC104rVylY7PA3IQY3SYU3OZTytsPg2gwQ-y9QWoZpe8f4TsEcV9CIoyu1K3dQCMtlMuUaBaWTVxQ8iK0YaK-3PPQPSAPXWK+KxKoDMojyD4dtMLH-BLQSxS7eZPdU-ERuVvH3VY545q3XfAEPMPLq0uZEowKiMjfUCY9QbWJgnUKLLSpMCapWKagPAvPo+S9+AwDQ6pTsGwa9Z4RnMy13PDXeF7Qq9yyajvDwbvSAGcrquSZakoe8SBQke6CMBUba7k2occFEhwBwIAA */
  tsTypes: {} as import('./machine.typegen').Typegen0,
  schema: {
    context: {} as NarrationMachineContext,
    events: {} as NarrationMachineActions,
  },
  context: initialContext,
  predictableActionArguments: true,
  initial: 'idle',
  states: {
    idle: {
      entry: [assign(() => initialContext), 'onIdle'],
      on: {
        INIT: {
          target: 'init',
          actions: assign((_, event) => event.data),
        },
      },
    },
    init: {
      invoke: {
        src: getMediaStream,
        onDone: {
          target: 'mediaRecorder',
          actions: assign((_, event) => event.data),
        },
        onError: 'mediaError',
      },
    },
    mediaError: {
      entry: ['onMediaError'],
      always: { target: 'idle' },
    },
    mediaRecorder: {
      initial: 'idle',
      invoke: {
        src: createMediaRecorder,
      },
      states: {
        idle: {
          on: {
            CREATED: {
              target: 'active',
              actions: assign({
                mediaRecorderProxy: (_, event) => event.data,
              }),
            },
          },
        },
        active: {
          type: 'parallel',
          entry: ['onMediaActive'],
          on: {
            NETWORK_ERROR: 'completedWhenNetworkError',
          },
          states: {
            recorder: {
              initial: 'idle',
              states: {
                idle: {
                  entry: assign({
                    blobs: [] as Blob[],
                    buffer: new Uint8Array(),
                    lastBlobIndex: 0,
                    lastBufferChunk: 0,
                    retry: 0,
                    uploadId: '',
                    uploadUrl: '',
                  }),
                  on: {
                    START: {
                      target: 'running',
                      actions: onStart,
                    },
                    STOP: {
                      target: 'completed',
                      actions: [
                        assign({
                          isCancelledOnStart: true,
                        }),
                        onClear,
                      ],
                    },
                  },
                },
                paused: {
                  on: {
                    RESUME: {
                      target: 'running',
                      actions: onResume,
                    },
                    STOP: {
                      target: 'completed',
                      actions: onStop,
                    },
                    CANCEL: {
                      target: 'completed',
                      actions: [
                        assign({
                          isCancelled: true,
                        }),
                        onStop,
                      ],
                    },
                    RESTART: {
                      target: 'idle',
                      actions: [onRestart, 'onRecorderRestarted'],
                    },
                  },
                },
                running: {
                  on: {
                    PAUSE: {
                      target: 'paused',
                      actions: onPause,
                    },
                    STOP: {
                      target: 'completed',
                      actions: onStop,
                    },
                    CANCEL: {
                      target: 'completed',
                      actions: [
                        assign({
                          isCancelled: true,
                        }),
                        onStop,
                      ],
                    },
                    RESTART: {
                      target: 'idle',
                      actions: [onRestart, 'onRecorderRestarted'],
                    },
                  },
                },
                completed: {
                  always: [
                    {
                      cond: (ctx) => ctx.isCancelledOnStart || ctx.isCancelled,
                      target: 'completedWhenOnCancel',
                    },
                    {
                      target: 'completedWhenOnStop',
                    },
                  ],
                },
                completedWhenOnStop: {
                  entry: 'onRecorderStopped',
                  type: 'final',
                },
                completedWhenOnCancel: {
                  type: 'final',
                },
              },
            },
            resumableUploader: {
              initial: 'idle',
              on: {
                BLOB: {
                  actions: assign({
                    blobs: (context, event) => [...context.blobs, event.data],
                  }),
                },
              },
              states: {
                idle: {
                  on: {
                    START_UPLOADING: {
                      target: 'active',
                    },
                    STOP: 'completed',
                  },
                },
                active: {
                  initial: 'generatingUploadSession',
                  on: {
                    CANCEL: 'completed',
                    RESTART: 'idle',
                  },
                  states: {
                    generatingUploadSession: {
                      on: { STOP: 'completed' },
                      invoke: {
                        src: generateUploadSession,
                        onDone: {
                          target: 'transforming',
                          actions: [
                            assign((_, event) => ({ ...event.data, retry: 0 })),
                            'onUploadedSessionReady',
                          ],
                        },
                        onError: [
                          {
                            target: 'delayUploadSession',
                            actions: assign({
                              retry: (context) => context.retry + 1,
                            }),
                          },
                        ],
                      },
                    },
                    delayUploadSession: {
                      on: { STOP: 'completed' },
                      after: [
                        {
                          delay: (context) => (context.retry ? 1000 : 0),
                          target: 'generatingUploadSession',
                        },
                      ],
                    },
                    transforming: {
                      invoke: {
                        src: transformChunks,
                        onDone: {
                          target: 'checkUploadingState',
                          actions: assign((_, event) => event.data),
                        },
                      },
                    },
                    checkUploadingState: {
                      always: [
                        {
                          target: 'uploadingChunkFinal',
                          cond: (context) =>
                            context.mediaRecorderProxy?.state === 'stopped',
                        },
                        {
                          target: 'uploadingChunk',
                        },
                      ],
                    },
                    uploadingChunk: {
                      invoke: {
                        src: uploadChunk,
                        onDone: {
                          target: 'transforming',
                          actions: assign((_, event) => ({
                            ...event.data,
                            retry: 0,
                          })),
                        },
                        onError: [
                          {
                            target: 'delayUploadingChunk',
                            actions: [
                              assign({
                                retry: (context) => context.retry + 1,
                              }),
                            ],
                          },
                        ],
                      },
                    },
                    delayUploadingChunk: {
                      after: [
                        {
                          delay: (context) => (context.retry ? 1000 : 0),
                          target: 'uploadingChunk',
                        },
                      ],
                    },
                    uploadingChunkFinal: {
                      initial: 'idle',
                      states: {
                        idle: {
                          invoke: {
                            src: uploadChunkFinal,
                            onDone: [
                              {
                                cond: (_, event) => event.data.isFinal,
                                target: 'completed',
                                actions: assign({
                                  retry: 0,
                                }),
                              },
                              {
                                actions: assign((_, event) => ({
                                  ...event.data,
                                  retry: 0,
                                })),
                                target: 'idle',
                              },
                            ],
                            onError: [
                              {
                                target: 'delayUploadingChunkFinal',
                                actions: assign({
                                  retry: (context) => context.retry + 1,
                                }),
                              },
                            ],
                          },
                        },
                        delayUploadingChunkFinal: {
                          after: [
                            {
                              delay: (context) => (context.retry ? 1000 : 0),
                              target: 'idle',
                            },
                          ],
                        },
                        completed: { type: 'final' },
                      },
                      onDone: 'completed',
                    },
                    completed: { type: 'final' },
                  },
                  onDone: 'completed',
                },
                completed: { type: 'final' },
              },
            },
          },
          onDone: 'completed',
        },
        completed: {
          type: 'final',
        },
        completedWhenNetworkError: {
          entry: [
            assign({
              isCancelledOnNetworkError: true,
            }),
            onStop,
          ],
          type: 'final',
        },
      },
      onDone: 'completed',
    },
    completed: {
      always: [
        {
          cond: (context) => context.isCancelledOnNetworkError,
          target: 'completedWhenCancelledOnNetworkError',
        },
        {
          cond: (context) => context.isCancelledOnStart,
          target: 'completedWhenCancelledOnStart',
        },
        {
          cond: (context) => context.isCancelled,
          target: 'completedWhenCancelled',
        },
        {
          target: 'completedWhenFinishedUploading',
        },
      ],
    },
    completedWhenCancelledOnNetworkError: {
      entry: ['onCompletedWhenCancelledOnNetworkError'],
      always: { target: 'idle' },
    },
    completedWhenCancelledOnStart: {
      entry: ['onCompletedWhenCancelledOnStart'],
      always: { target: 'idle' },
    },
    completedWhenCancelled: {
      entry: ['onCompletedWhenCancelled'],
      always: { target: 'idle' },
    },
    completedWhenFinishedUploading: {
      entry: ['onCompletedWhenFinishedUploadng'],
      always: { target: 'idle' },
    },
  },
});
